import { useCallback } from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';

import { moment } from '@hotjar/webapp/lib/moment';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { useLocalStorageJSONValue } from '@hotjar/webapp/hooks/useLocalStorageValue';
import { useAccountTrialInfo, useOwnedAccount } from '@hotjar/account';
import { useCurrentUser } from '@hotjar/user';
import { useCurrentSite } from '@hotjar/site';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { startYearlyUpsell } from '@hotjar/checkout';
import { PBReferrers } from '@hotjar/webapp/features/Billing/ContextualBanner/types';
import { toPlansAndBillingWithContext } from '@hotjar/webapp/features/Billing/ContextualBanner/utils';
import { upsell as openUpsellModal } from '@hotjar/webapp/domains/revenue/upsell';

import { noop } from '@hotjar/webapp/lib/function';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { TrialMessage } from './components/TrialMessage';
import { YearlyUpsellMessage } from './components/YearlyUpsellMessage';
import { GenericUpsellMessage } from './components/GenericUpsellMessage';

const YEARLY_MESSAGE_DISMISSED_STORAGE_KEY = 'dashboardTopBarMessageDismissed';

interface TopBarMessageProps {
  featureGroup: FeatureGroup;
  alternateStyling?: boolean;
}

enum MessageType {
  BASIC = 'basic',
  TRIAL = 'trial',
  PLUS = 'plus',
  YEARLY = 'yearly',
  MULTI = 'multi',
}

const UpsellLinkContainer = styled.div`
  display: inline-block;
  margin-left: ${tokens.spacer.x1};
  transition: all 0.2s ease-in-out;
  ${tokens.text('caption')}
`;

interface FeatureGroupUpsellProps {
  onUpsellClick: () => unknown;
  alternateStyling?: boolean;
}

const FeatureGroupUpsell = ({
  onUpsellClick,
  alternateStyling,
}: FeatureGroupUpsellProps): JSX.Element => {
  const { t } = useTranslationWithPrefix('common:sitebar.top-bar-message');

  return (
    <GenericUpsellMessage alternateStyling={alternateStyling} onUpsellClick={onUpsellClick}>
      {t('manage-your-plan')}
    </GenericUpsellMessage>
  );
};

const MultiProductUpsell = ({
  onUpsellClick,
  alternateStyling,
}: FeatureGroupUpsellProps): JSX.Element => {
  const { t } = useTranslationWithPrefix('common:sitebar.top-bar-message');

  return (
    <GenericUpsellMessage alternateStyling={alternateStyling} onUpsellClick={onUpsellClick}>
      {t('multi-product-upsell-cta')}
    </GenericUpsellMessage>
  );
};

// TODO: The alternateStyling prop should be removed once we migrate to the new
// navbar design. The only reason for having this prop is to ensure design consistency
// with the legacy as well as updated Hotjar UI
export const TopBarMessage = ({
  featureGroup,
  alternateStyling,
}: TopBarMessageProps): JSX.Element | null => {
  const { i18n } = useTranslation();
  const [yearlyMessageDismissed, setYearlyMessageDismissed] = useLocalStorageJSONValue<boolean>(
    YEARLY_MESSAGE_DISMISSED_STORAGE_KEY,
  );

  const { active: isActiveTrial = false, daysRemaining: daysRemainingRaw = 0 } =
    useAccountTrialInfo() ?? {};
  const daysRemaining = daysRemainingRaw ? Math.floor(daysRemainingRaw) : 0;
  const { data: currentSite } = useCurrentSite();
  const { data: currentUser } = useCurrentUser();
  const { data: ownedAccount } = useOwnedAccount();

  const handleMultiProductUpsell = useCallback(() => {
    if (!ownedAccount || !currentSite) {
      return;
    }
    openUpsellModal({
      site: currentSite,
      referrer: 'navigation.top-nav.upgrade-link',
    }).then(noop, noop);
  }, [ownedAccount, currentSite]);

  const handleUpsellToYearly = useCallback(() => {
    if (!ownedAccount || !currentSite) {
      return;
    }

    startYearlyUpsell(ownedAccount, currentSite, 'Top Bar Message', i18n?.resolvedLanguage ?? 'en');
  }, [ownedAccount, currentSite]);

  const handleUpsellToBusiness = useCallback(() => {
    if (!ownedAccount || !currentSite) {
      return;
    }

    toPlansAndBillingWithContext({
      referrer: PBReferrers.MANAGE_PLANS,
    });
  }, [currentSite, ownedAccount]);

  const handleTrialMessageClick = useCallback(() => {
    if (!ownedAccount) {
      return;
    }

    toPlansAndBillingWithContext({
      referrer: PBReferrers.IN_TRIAL,
      i18nData: {
        daysRemaining,
      },
    });
  }, [ownedAccount, daysRemaining]);

  const handleYearlyMessageDismiss = useCallback(() => {
    setYearlyMessageDismissed(true);
  }, [setYearlyMessageDismissed]);

  const isOwner = currentUser ? currentSite?.account?.adminIds?.includes(currentUser.id) : false;
  const isBillingV2 = ownedAccount?.billingVersion === 2;
  const isBillingV3 = isBillingV3Enabled(currentSite?.account);
  const isMonthly = ownedAccount?.billingCycle === 'monthly';
  const paymentMethodAvailable = !!ownedAccount?.paymentMethodType;
  const becameACustomer: number | null = currentUser?.becameACustomer || null;
  const daysSinceBecomingACustomer = becameACustomer
    ? Math.abs(moment.unix(becameACustomer).diff(new Date(), 'days'))
    : 0;

  const shouldShowBasicMessage = featureGroup === 'basic';
  const shouldShowTrialMessage = isBillingV2 && isMonthly && isActiveTrial;
  const shouldShowPlusMessage = featureGroup === 'plus' && paymentMethodAvailable;
  const shouldShowYearlyMessage =
    isBillingV2 &&
    isMonthly &&
    paymentMethodAvailable &&
    daysSinceBecomingACustomer > 30 &&
    !yearlyMessageDismissed;

  const determineMessageType = (): MessageType | null => {
    if (isBillingV3 && isOwner) {
      return MessageType.MULTI;
    }

    if (shouldShowBasicMessage) {
      return MessageType.BASIC;
    }

    if (shouldShowTrialMessage) {
      return MessageType.TRIAL;
    }

    if (shouldShowPlusMessage) {
      return MessageType.PLUS;
    }

    if (shouldShowYearlyMessage) {
      return MessageType.YEARLY;
    }

    return null;
  };
  const messageType = determineMessageType();

  if (!messageType || !isOwner) {
    return null;
  }

  const isBasicOrPlusType = messageType === MessageType.BASIC || messageType === MessageType.PLUS;
  return (
    <UpsellLinkContainer data-testid="top-bar-message">
      {messageType === MessageType.MULTI ? (
        <MultiProductUpsell
          onUpsellClick={handleMultiProductUpsell}
          alternateStyling={alternateStyling}
        />
      ) : null}
      {isBasicOrPlusType ? (
        <FeatureGroupUpsell
          onUpsellClick={handleUpsellToBusiness}
          alternateStyling={alternateStyling}
        />
      ) : null}
      {messageType === MessageType.TRIAL && (
        <TrialMessage
          alternateStyling={alternateStyling}
          handleTrialMessageClick={handleTrialMessageClick}
          daysRemaining={daysRemaining}
        />
      )}
      {messageType === MessageType.YEARLY && (
        <YearlyUpsellMessage
          handleUpsellToYearly={handleUpsellToYearly}
          handleYearlyMessageDismiss={handleYearlyMessageDismiss}
          alternateStyling={alternateStyling}
        />
      )}
    </UpsellLinkContainer>
  );
};
